// frontend/src/screens/ChangePassword.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { changePassword } from '../services/authApi';
import { localizedValue } from '../localization/presentationLanguage';
import { getAuthToken } from '../utils/auth';

const copy = (es, en) => localizedValue({ es, en });

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [msg, setMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [focusCurr, setFocusCurr] = useState(false);
  const [focusNew, setFocusNew] = useState(false);
  const [focusConfirm, setFocusConfirm] = useState(false);
  const navigate = useNavigate();

  const cardRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    setIsSuccess(false);
    try {
      const token = getAuthToken();
      if (!token) {
        setMsg(copy('Debes iniciar sesión.', 'You must sign in.'));
        return;
      }
      if (!currentPassword) {
        setMsg(copy('Ingresá tu contraseña actual.', 'Enter your current password.'));
        return;
      }
      if (!newPassword) {
        setMsg(copy('Ingresá una nueva contraseña.', 'Enter a new password.'));
        return;
      }
      if (newPassword !== confirmNewPassword) {
        setMsg(copy('La confirmación no coincide con la nueva contraseña.', 'The confirmation does not match the new password.'));
        return;
      }
      // Validación básica de fuerza
      const strong = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;
      if (!strong.test(newPassword)) {
        setMsg(copy('La nueva contraseña debe tener al menos 8 caracteres, con letras y números.', 'The new password must contain at least 8 characters, including letters and numbers.'));
        return;
      }

      await changePassword(token, { currentPassword, newPassword });
      setIsSuccess(true);
      setMsg(copy('Contraseña actualizada correctamente ✅', 'Password updated successfully ✅'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      setIsSuccess(false);
      setMsg(copy(err.message || 'Error cambiando la contraseña.', 'The password could not be changed.'));
    }
  };

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.innerWidth <= 600) {
        const fix = () => {
          const el = cardRef.current;
          if (!el) return;
          const r = el.getBoundingClientRect();
          const target = Math.max(0, window.scrollY + r.top - 12);
          window.scrollTo({ top: target, left: 0, behavior: 'auto' });
        };
        fix();
        const rafId = requestAnimationFrame(fix);
        const t1 = setTimeout(fix, 120);
        const t2 = setTimeout(fix, 360);
        window.addEventListener('load', fix, { once: true });
        return () => {
          cancelAnimationFrame(rafId);
          clearTimeout(t1);
          clearTimeout(t2);
          window.removeEventListener('load', fix);
        };
      }
    } catch {}
  }, []);

  return (
    <>
      <style>{`
        /* Desactivar pseudo-capas de edge-blur si este screen hereda la clase */
        #changePwdBgHost.edge-blur-bg::before,
        #changePwdBgHost.edge-blur-bg::after { content: none !important; }

        @keyframes slowSpinChangePwd {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to   { transform: translate(-50%, -50%) rotate(360deg); }
        }

        /* Vignette sutil para profundidad */
        #changePwdBgHost .vignette {
          position: fixed;
          inset: 0;
          z-index: 2;
          pointer-events: none;
          background: radial-gradient(120vmax 120vmax at 50% 45%, transparent 0 62%, rgba(0,0,0,0.10) 90%, rgba(0,0,0,0.18) 100%);
        }

        /* Ajustes responsive del fondo giratorio */
        #changePwdBgHost .spin-bg { width: 90vmin; height: 90vmin; opacity: 1; }
        @media (max-width: 600px) {
          #changePwdBgHost .spin-bg { width: 84vmin; height: 84vmin; opacity: .9; animation-duration: 120s; }
        }
        @media (max-width: 420px) {
          #changePwdBgHost .spin-bg { width: 80vmin; height: 80vmin; opacity: .88; }
        }
        @media (prefers-reduced-motion: reduce) {
          #changePwdBgHost .spin-bg { animation: none !important; transform: translate(-50%, -50%); }
        }

        /* Base fallback (mismo violeta Welcome/Login) */
        #changePwdBgHost {
          background: radial-gradient(120% 100% at 50% 30%, rgba(255,215,0,0.12), rgba(75,0,130,0.18)),
                      linear-gradient(180deg, #3b0a70 0%, #4a0f90 100%);
        }

        /* Halo dorado + blur sutil (igual Login) */
        #changePwdBgHost::before {
          content: '';
          position: fixed;
          inset: 0;
          z-index: 1;
          background: radial-gradient(100vmax 100vmax at 52% 46%, rgba(218,165,32,0.12) 0%, rgba(218,165,32,0.06) 30%, rgba(106,13,173,0.05) 55%, transparent 70%);
          backdrop-filter: blur(3px) saturate(1.06) brightness(1.06);
          -webkit-backdrop-filter: blur(3px) saturate(1.06) brightness(1.06);
          mix-blend-mode: soft-light;
          pointer-events: none;
        }

        /* Estrellas animadas (igual Login) */
        #changePwdBgHost::after {
          content: '';
          position: fixed;
          inset: 0;
          z-index: 0;
          background: radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.9), transparent 60%),
                      radial-gradient(1px 1px at 80% 70%, rgba(255,255,255,0.7), transparent 60%),
                      radial-gradient(1px 1px at 50% 10%, rgba(255,255,255,0.8), transparent 60%),
                      radial-gradient(1px 1px at 10% 90%, rgba(255,255,255,0.6), transparent 60%);
          background-repeat: repeat;
          background-size: 300px 300px;
          animation: twinkle 12s linear infinite;
          opacity: 0.6;
          pointer-events: none;
        }

        @keyframes twinkle {
          0%, 100% { opacity: 0.6; transform: translateY(0); }
          50% { opacity: 0.9; transform: translateY(-2px); }
        }

        @media (prefers-reduced-motion: reduce) {
          #changePwdBgHost::after { animation: none !important; }
        }

        @media (prefers-reduced-motion: reduce) { .no-spin { animation: none !important; } }
      `}</style>
      <div id="changePwdBgHost" className="edge-blur-bg" role="main" style={styles.page} aria-labelledby="changePwdTitle">
        {/* Rueda giratoria (misma imagen) */}
        <div aria-hidden="true" className="spin-bg" style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundImage: 'url("/logo/Zodiacus.jpeg")',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          borderRadius: '50%',
          pointerEvents: 'none',
          opacity: 1,
          filter: 'brightness(1.08) contrast(1.12) saturate(1.08)',
          boxShadow: '0 0 60px rgba(0,0,0,0.18), inset 0 0 10px rgba(0,0,0,0.12)',
          willChange: 'transform',
          transformOrigin: 'center center',
          zIndex: 0,
          animation: 'slowSpinChangePwd 140s linear infinite',
        }} />

        <div className="vignette" aria-hidden="true" />
      <div ref={cardRef} style={styles.card}>
        {/* Header con volver siempre visible */}
        <div style={styles.header}>
          <button
            type="button"
            onClick={() => {
              const canGoBack = typeof window !== 'undefined' && window.history?.state && window.history.state.idx > 0;
              if (canGoBack) navigate(-1);
              else navigate('/home');
            }}
            style={styles.btnBack}
            aria-label={copy('Volver', 'Back')}
            onMouseEnter={(e)=>{
              e.currentTarget.style.transform='translateY(-1px) scale(1.02)';
              e.currentTarget.style.boxShadow='0 8px 20px rgba(218,165,32,0.28)';
              e.currentTarget.style.backgroundImage='linear-gradient(90deg, #DAA520 0%, #FFD54F 100%)';
            }}
            onMouseLeave={(e)=>{
              e.currentTarget.style.transform='none';
              e.currentTarget.style.boxShadow=styles.btnBack.boxShadow;
              e.currentTarget.style.backgroundImage='';
            }}
          >
            ← {copy('Volver', 'Back')}
          </button>

          <div style={{ flex: 1, textAlign: 'center' }}>
            <h2 id="changePwdTitle" style={styles.title}>{copy('Cambiar contraseña', 'Change password')}</h2>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <label htmlFor="currentPassword" style={styles.label}>{copy('Contraseña actual', 'Current password')}</label>
          <input
            id="currentPassword"
            name="currentPassword"
            type="password"
            placeholder={copy('Contraseña actual', 'Current password')}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            style={{ ...styles.input, ...(focusCurr ? styles.inputFocus : null) }}
            required
            autoComplete="current-password"
            onFocus={()=>setFocusCurr(true)}
            onBlur={()=>setFocusCurr(false)}
          />

          <label htmlFor="newPassword" style={styles.label}>{copy('Nueva contraseña', 'New password')}</label>
          <input
            id="newPassword"
            name="newPassword"
            type="password"
            placeholder={copy('Nueva contraseña', 'New password')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            style={{ ...styles.input, ...(focusNew ? styles.inputFocus : null) }}
            required
            autoComplete="new-password"
            minLength={8}
            pattern="^(?=.*[A-Za-z])(?=.*\d).{8,}$"
            title={copy('Mínimo 8 caracteres, con al menos una letra y un número', 'At least 8 characters, including one letter and one number')}
            onFocus={()=>setFocusNew(true)}
            onBlur={()=>setFocusNew(false)}
          />

          <label htmlFor="confirmNewPassword" style={styles.label}>{copy('Confirmar nueva contraseña', 'Confirm new password')}</label>
          <input
            id="confirmNewPassword"
            name="confirmNewPassword"
            type="password"
            placeholder={copy('Confirmá la nueva contraseña', 'Confirm the new password')}
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            style={{ ...styles.input, ...(focusConfirm ? styles.inputFocus : null) }}
            required
            autoComplete="new-password"
            minLength={8}
            pattern="^(?=.*[A-Za-z])(?=.*\d).{8,}$"
            title={copy('Mínimo 8 caracteres, con al menos una letra y un número', 'At least 8 characters, including one letter and one number')}
            onFocus={()=>setFocusConfirm(true)}
            onBlur={()=>setFocusConfirm(false)}
          />

          <small style={styles.hint}>
            {copy('Tip: Usá una frase o mezcla de letras y números. Evitá usar contraseñas comunes o repetidas.', 'Tip: Use a phrase or a mix of letters and numbers. Avoid common or reused passwords.')}
          </small>

          <button
            type="submit"
            style={styles.btnPrimary}
            onMouseEnter={(e)=>{
              e.currentTarget.style.transform='translateY(-1px)';
              e.currentTarget.style.boxShadow='0 12px 26px rgba(218,165,32,0.38)';
              e.currentTarget.style.backgroundImage='linear-gradient(135deg, #DAA520, #FFD54F)';
            }}
            onMouseLeave={(e)=>{
              e.currentTarget.style.transform='none';
              e.currentTarget.style.boxShadow=styles.btnPrimary.boxShadow;
              e.currentTarget.style.backgroundImage='';
            }}
          >
            {copy('Guardar', 'Save')}
          </button>
        </form>

        {msg && <p role="status" aria-live="polite" style={{ ...styles.msg, ...(isSuccess ? styles.msgSuccess : styles.msgError) }}>{msg}</p>}
      </div>
    </div>
    </>
  );
};

const styles = {
  page: {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    position: 'relative',
    isolation: 'isolate',
    overflow: 'hidden',
    background: 'transparent',
  },
  card: {
    position: 'relative',
    zIndex: 1,
    background: 'rgba(255,255,255,0.72)', // más transparente
    backdropFilter: 'blur(8px) saturate(1.1)', // efecto vidrio para legibilidad
    padding: 22,
    borderRadius: 16,
    maxWidth: 460,
    width: '100%',
    boxShadow: '0 22px 60px rgba(44,13,78,0.20), 0 2px 8px rgba(218,165,32,0.10)',
    border: '1px solid #DAA52022',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
    borderBottom: '1px solid rgba(75,0,130,0.14)',
    paddingBottom: 8,
  },
  title: {
    margin: 0,
    color: '#4B0082',
    fontWeight: 900,
    letterSpacing: '0.2px',
    textShadow: '0 1px 10px rgba(75,0,130,0.08)'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 10,
  },
  label: {
    fontSize: 13,
    fontWeight: 800,
    color: '#4B0082',
  },
  input: {
    padding: '12px 14px',
    borderRadius: 12,
    border: '1px solid #4B008233',
    fontSize: 15,
    background: '#FFF8E1',
    color: '#2d084c',
    boxShadow: 'inset 0 1px 2px rgba(218,165,32,0.12)',
    outline: 'none',
    transition: 'border .18s, box-shadow .18s, background .18s',
  },
  inputFocus: {
    border: '1px solid #DAA520',
    boxShadow: '0 0 0 3px rgba(218,165,32,0.25), inset 0 1px 2px rgba(218,165,32,0.12)',
    background: '#FFF3C4',
  },
  btnPrimary: {
    marginTop: 6,
    padding: '12px 14px',
    border: '1px solid #4B008222',
    borderRadius: 12,
    background: 'linear-gradient(135deg, #DAA520, #f4c84a)',
    color: '#4B0082',
    fontWeight: 900,
    cursor: 'pointer',
    boxShadow: '0 10px 24px rgba(218,165,32,0.28)',
    transition: 'transform .12s ease, box-shadow .12s ease',
  },
  btnBack: {
    padding: '10px 12px',
    borderRadius: 12,
    border: '1px solid #4B008222',
    background: 'linear-gradient(90deg, #DAA520 0%, #FFD54F 100%)',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 800,
    minWidth: 110,
    textAlign: 'left',
    boxShadow: '0 4px 12px rgba(75,0,130,0.10)',
    transition: 'transform .12s ease, box-shadow .12s ease',
  },
  msg: {
    marginTop: 12,
    color: '#4B0082',
    textAlign: 'center',
    fontWeight: 800,
    background: 'linear-gradient(180deg, rgba(218,165,32,0.12), rgba(255,255,255,1))',
    border: '1px solid #DAA52055',
    borderRadius: 12,
    padding: '10px 12px',
  },
  msgSuccess: {
    color: '#1f6f3f',
    background: 'linear-gradient(180deg, rgba(46,125,50,0.16), rgba(255,255,255,1))',
    border: '1px solid rgba(46,125,50,0.45)',
  },
  msgError: {
    color: '#7a1020',
    background: 'linear-gradient(180deg, rgba(220,53,69,0.12), rgba(255,255,255,1))',
    border: '1px solid rgba(220,53,69,0.35)',
  },
  hint: {
    fontSize: 12,
    color: '#5a4a8a',
    marginTop: 2,
  },
};

export default ChangePassword;
